import { ApiCurrency, ApiSimpleUTM } from "@incendium/api";
import {
  addProject,
  fetchAllClientsAndProjectsThunk,
  updateProject,
} from "Actions";
import GlassCard from "Components/GlassCard/GlassCard";
import {
  ProjectDomains,
  ProjectOptionsDrawerForm,
  saveProject,
  saveProjectSettings,
} from "features/project";
import {
  bulkDeleteUTMs,
  bulkSaveUTMs,
} from "features/utms/services/utmService";
import { compareUtms } from "features/utms/utils";
import { useNotification, useSelectedProject } from "Hooks";
import { useUTMs } from "Hooks/useUTMs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { ValidatorForm } from "react-material-ui-form-validator";
import ProjectFormInner from "features/project/components/ProjectFormInner";
import useAppendSearchState from "Hooks/useAppendSearchState";
import ConsentModeSettings from "features/consent/components/ConsentModeSettings";

function GeneralSettingsPage() {
  const { projectSettings, setProjectSettings, selectedProject } =
    useSelectedProject();
  const { utms, refetch: refetchUtms } = useUTMs();
  const [originalUtms, setOriginalUtms] = useState<ApiSimpleUTM[] | null>(null);
  const [name, setName] = useState(selectedProject?.name || "");
  const [overview, setOverview] = useState(selectedProject?.overview || "");
  const [timezone, setTimezone] = useState(selectedProject?.timezone || "");
  const [currency, setCurrency] = useState(
    selectedProject?.currency || ApiCurrency.USD,
  );
  const [domains, setDomains] = useState(
    (selectedProject?.domains || []).filter((d) => d.isPrimary) || [],
  );
  const [disabled, setDisabled] = useState(false);
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const dispatch = useDispatch();
  const [tab, setTab] = useAppendSearchState("tab", "general");
  useEffect(() => {
    if (selectedProject && utms?.length > 0 && !originalUtms) {
      setOriginalUtms(utms);
    }
  }, [selectedProject, utms, originalUtms]);

  const tabs = useMemo(
    () => [
      { label: "General", value: "general" },
      { label: "Advanced", value: "advanced" },
      { label: "Compliance & Consent", value: "consent" },
    ],
    [],
  );

  const onProjectSubmit = useCallback(async () => {
    setDisabled(true);
    try {
      const res = await saveProject(selectedProject?.clientId!, {
        id: selectedProject?.id,
        name,
        overview,
        timezone,
        domains,
        currency,
      });
      if (selectedProject?.id) {
        dispatch(updateProject(res));
      } else {
        dispatch(addProject(res));
      }
      showSuccessNotification(`${res.name} Saved`);
    } catch (e) {
      showErrorNotification("Internal Server Error");
    } finally {
      setDisabled(false);
    }
  }, [
    selectedProject,
    showSuccessNotification,
    showErrorNotification,
    dispatch,
    name,
    overview,
    timezone,
    domains,
    currency,
  ]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await saveProjectSettings(projectSettings, selectedProject!);
      const { utmsToSave, utmsToDelete } = compareUtms(
        originalUtms || [],
        utms,
      );
      if (utmsToSave && utmsToSave.length > 0) {
        await bulkSaveUTMs(selectedProject!.id!, utmsToSave);
      }
      if (utmsToDelete && utmsToDelete.length > 0) {
        await bulkDeleteUTMs(
          selectedProject!.id!,
          utmsToDelete.map((u) => u.id!),
        );
      }

      setOriginalUtms(null);
      refetchUtms();
      showSuccessNotification(`${selectedProject!.name} Settings Saved`);
      dispatch(
        fetchAllClientsAndProjectsThunk({
          clientId: selectedProject!.clientId as number,
          projectId: selectedProject!.id as number,
        }),
      );
    } catch (error) {
      showErrorNotification(
        `Internal Error saving Project settings, please try again`,
      );
    }
  };

  return (
    <Stack spacing={2} mb={2}>
      <Tabs value={tab} onChange={(e, value) => setTab(value)}>
        {tabs.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Tabs>
      {tab === "general" && (
        <GlassCard sx={{ width: 700 }} boxProps={{ px: 0, pt: 4 }}>
          <ValidatorForm onSubmit={onProjectSubmit}>
            <Box px={3}>
              <ProjectFormInner
                name={name}
                setName={setName}
                timezone={timezone}
                setTimezone={setTimezone}
                currency={currency}
                setCurrency={setCurrency}
                overview={overview}
                setOverview={setOverview}
                disabled={disabled}
              />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box px={3}>
              <Typography variant="subtitle2">
                Manage your projects domains
              </Typography>
              <Typography variant="body2" mb={3}>
                These are the domain you shall run incendium from
              </Typography>
              <ProjectDomains domains={domains} setDomains={setDomains} />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box px={3}>
              <Button
                disabled={disabled}
                variant="contained"
                color="primary"
                type="submit"
              >
                Update Project
              </Button>
            </Box>
          </ValidatorForm>
        </GlassCard>
      )}
      {tab === "advanced" && (
        <GlassCard sx={{ width: 700 }} boxProps={{ p: 0 }}>
          <ProjectOptionsDrawerForm
            onSubmit={onSubmit}
            projectSettings={projectSettings}
            setProjectSettings={setProjectSettings}
          />
        </GlassCard>
      )}
      {tab === "consent" && (
        <GlassCard sx={{ width: 700 }} boxProps={{ px: 3, py: 3 }}>
          <ConsentModeSettings />
        </GlassCard>
      )}
    </Stack>
  );
}

export default GeneralSettingsPage;
